'use client';

import { useEffect, useState, useCallback } from 'react';

const POLL_INTERVAL_MS = 2 * 60 * 1000; // 2 minutes

export default function VersionChecker() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const buildSha = process.env.NEXT_PUBLIC_BUILD_SHA || '';

  const check = useCallback(async () => {
    if (!buildSha) return; // disabled in dev / no build SHA available
    if (updateAvailable) return; // already flagged, no need to keep checking
    try {
      const res = await fetch('/api/version', { cache: 'no-store' });
      if (!res.ok) return;
      const data = (await res.json()) as { sha?: string };
      if (data.sha && data.sha !== buildSha) {
        setUpdateAvailable(true);
      }
    } catch {
      // network blip — try again next interval
    }
  }, [buildSha, updateAvailable]);

  useEffect(() => {
    if (!buildSha) return;

    // Initial check
    check();

    // Polling while tab is visible
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        check();
      }
    }, POLL_INTERVAL_MS);

    // Snappy check when user comes back to the tab
    const onVisibility = () => {
      if (document.visibilityState === 'visible') check();
    };
    const onFocus = () => check();

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
    };
  }, [buildSha, check]);

  if (!updateAvailable || dismissed) return null;

  return (
    <div
      role="status"
      style={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        zIndex: 10000,
        background: '#1a1a1a',
        color: '#FAF9F5',
        padding: '12px 16px',
        borderRadius: 12,
        boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        fontFamily: 'Lato, -apple-system, sans-serif',
        fontSize: 13,
        maxWidth: 360,
      }}
    >
      <span>Nueva versión disponible</span>
      <button type="button"
        onClick={() => window.location.reload()}
        style={{
          background: '#FAF9F5',
          color: '#1a1a1a',
          border: 'none',
          padding: '6px 12px',
          borderRadius: 8,
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: '0.5px',
          textTransform: 'uppercase',
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        Actualizar
      </button>
      <button type="button"
        onClick={() => setDismissed(true)}
        aria-label="Cerrar"
        style={{
          background: 'transparent',
          color: 'rgba(250,249,245,0.6)',
          border: 'none',
          fontSize: 18,
          lineHeight: 1,
          cursor: 'pointer',
          padding: '0 4px',
          fontFamily: 'inherit',
        }}
      >
        ✕
      </button>
    </div>
  );
}
